<template>
  <section class="result-documents">
    <header class="rd-head">
      <div>
        <strong>Résultats en document (PDF / image)</strong>
        <small>Pour les résultats difficiles à saisir dans le formulaire, joignez le compte rendu ou le cliché.</small>
      </div>
      <label v-if="!readonly" class="btn btn-outline-primary rd-add" :class="{ 'is-busy': uploading }">
        <i class="fa fa-paperclip"></i> Joindre
        <input type="file" accept="application/pdf,image/png,image/jpeg" multiple :disabled="uploading" @change="onFilesSelected" hidden>
      </label>
    </header>

    <p v-if="error" class="rd-error" role="alert">{{ error }}</p>
    <p v-if="loading" class="rd-muted">Chargement…</p>
    <p v-else-if="!documents.length" class="rd-muted">Aucun document joint.</p>

    <ul v-else class="rd-list">
      <li v-for="doc in documents" :key="doc.id" class="rd-item">
        <i class="fa" :class="doc.mime === 'application/pdf' ? 'fa-file-pdf-o' : 'fa-file-image-o'"></i>
        <span class="rd-name" :title="doc.original_name">{{ doc.original_name }}</span>
        <span class="rd-meta">v{{ doc.version }} · {{ humanSize(doc.size_bytes) }}<template v-if="doc.uploaded_by_name"> · {{ doc.uploaded_by_name }}</template></span>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="openPreview(doc)">Voir</button>
        <button v-if="!readonly" type="button" class="btn btn-sm btn-outline-danger" @click="removeDoc(doc)">Retirer</button>
      </li>
    </ul>

    <div v-if="preview" class="rd-preview">
      <div class="rd-preview-bar">
        <strong>{{ preview.name }}</strong>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="closePreview">Fermer</button>
      </div>
      <iframe v-if="preview.isPdf" :src="preview.url" class="rd-frame" title="Aperçu du document"></iframe>
      <img v-else :src="preview.url" alt="Aperçu du résultat" class="rd-image">
    </div>
  </section>
</template>

<script>
import store_laboratoire from '@/store/mods/analyses/laboratoire'

export default {
  name: 'AnalyseResultDocuments',
  props: {
    demandeId: { type: [Number, String], default: null },
    readonly: { type: Boolean, default: false }
  },
  data () {
    return { documents: [], loading: false, uploading: false, error: '', preview: null, loadSequence: 0, previewSequence: 0 }
  },
  computed: { accessIdentity () { const getters = (this.$store && this.$store.getters) || {}; const user = getters.authInfo || {}; const role = getters.activeRole || {}; return [user.id, user.access_token, role.id, role.nom, JSON.stringify(role.permissions || [])].join(':') } },
  watch: {
    demandeId: { immediate: true, handler () { this.load() } },
    accessIdentity () { this.loadSequence++; this.documents = []; this.error = ''; this.closePreview(); this.load() }
  },
  beforeDestroy () { this.loadSequence++; this.closePreview() },
  methods: {
    async load () {
      const sequence = ++this.loadSequence
      this.documents = []; this.closePreview()
      if (!this.demandeId) { this.documents = []; return }
      this.loading = true; this.error = ''
      try {
        const response = await store_laboratoire.getDocuments(this.demandeId)
        if (sequence !== this.loadSequence) return
        this.documents = (response && response.data) || []
      } catch (e) { if (sequence === this.loadSequence) this.error = this.message(e, 'Impossible de charger les documents.') } finally { if (sequence === this.loadSequence) this.loading = false }
    },
    async onFilesSelected (event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length || !this.demandeId || this.readonly || this.uploading) return
      const request = this.demandeId; const identity = this.accessIdentity
      const formData = new FormData()
      files.forEach(file => formData.append('documents[]', file))
      this.uploading = true; this.error = ''
      try {
        await store_laboratoire.ajouterDocuments(request, formData)
        if (request !== this.demandeId || identity !== this.accessIdentity) return
        await this.load()
        this.$emit('uploaded')
      } catch (e) { if (request === this.demandeId && identity === this.accessIdentity) this.error = this.message(e, 'Le document n’a pas pu être enregistré.') } finally { this.uploading = false }
    },
    async openPreview (doc) {
      this.closePreview()
      const sequence = this.previewSequence; const request = this.demandeId; const identity = this.accessIdentity
      this.error = ''
      try {
        const blob = await store_laboratoire.getDocumentBlob(this.demandeId, doc.id)
        if (sequence !== this.previewSequence || request !== this.demandeId || identity !== this.accessIdentity) return
        this.preview = { url: URL.createObjectURL(blob), name: doc.original_name, isPdf: doc.mime === 'application/pdf' }
      } catch (e) { if (sequence === this.previewSequence) this.error = this.message(e, 'Le document n’a pas pu être ouvert.') }
    },
    closePreview () {
      this.previewSequence++
      if (this.preview && this.preview.url) URL.revokeObjectURL(this.preview.url)
      this.preview = null
    },
    async removeDoc (doc) {
      if (this.readonly || this.uploading) return
      if (!window.confirm('Retirer ce document ?')) return
      this.error = ''
      try { await store_laboratoire.supprimerDocument(this.demandeId, doc.id); await this.load() }
      catch (e) { this.error = this.message(e, 'Le document n’a pas pu être retiré.') }
    },
    humanSize (bytes) {
      const b = Number(bytes) || 0
      if (b < 1024) return b + ' o'
      if (b < 1048576) return (b / 1024).toFixed(0) + ' Ko'
      return (b / 1048576).toFixed(1) + ' Mo'
    },
    message (e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) ? e.response.data.message : fallback
    }
  }
}
</script>

<style scoped>
.result-documents { border: 1px solid #e5e9f2; border-radius: 8px; padding: 12px 14px; margin-top: 14px; background: #fbfcfe; }
.rd-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.rd-head small { display: block; color: var(--ui-text-secondary, #7b869c); font-size: 12px; }
.rd-add { cursor: pointer; white-space: nowrap; }
.rd-add.is-busy { opacity: .6; pointer-events: none; }
.rd-add input { display: none; }
.rd-muted { color: var(--ui-text-secondary, #7b869c); margin: 8px 0 0; }
.rd-error { color: #c0392b; margin: 8px 0 0; }
.rd-list { list-style: none; padding: 0; margin: 10px 0 0; }
.rd-item { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-top: 1px solid #eef1f7; }
.rd-item .fa { color: var(--ui-text, #4a5876); }
.rd-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rd-meta { color: var(--ui-text-secondary, #97a0b5); font-size: 12px; white-space: nowrap; }
.rd-preview { margin-top: 12px; border: 1px solid #e5e9f2; border-radius: 6px; overflow: hidden; }
.rd-preview-bar { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; background: #f2f5fb; }
.rd-frame { width: 100%; height: 520px; border: 0; }
.rd-image { display: block; max-width: 100%; margin: 0 auto; }
</style>
